import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Plus, Trash2, Clock, User, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface ContactAttempt {
  id: string;
  applicant_id: string;
  contact_type: string;
  notes: string | null;
  contacted_at: string;
  contacted_by: string;
  contacted_by_user_id: string | null;
  created_at: string;
}

interface ApplicantContactAttemptsProps {
  applicantId: string;
}

const CONTACT_TYPES = [
  { value: "Anruf", label: "Anruf" },
  { value: "E-Mail", label: "E-Mail" },
  { value: "SMS", label: "SMS" },
  { value: "WhatsApp", label: "WhatsApp" },
  { value: "Persönlich", label: "Persönlich" },
  { value: "Sonstiges", label: "Sonstiges" },
];

const ApplicantContactAttempts = ({ applicantId }: ApplicantContactAttemptsProps) => {
  const { user, profile } = useAuth();
  const [attempts, setAttempts] = useState<ContactAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    contact_type: "Anruf",
    notes: "",
    contacted_at: new Date().toISOString().slice(0, 16),
  });

  useEffect(() => {
    fetchAttempts();
  }, [applicantId]);

  const fetchAttempts = async () => {
    try {
      const { data, error } = await supabase
        .from("applicant_contact_attempts")
        .select("*")
        .eq("applicant_id", applicantId)
        .order("contacted_at", { ascending: false });

      if (error) throw error;
      setAttempts(data || []);
    } catch (error) {
      console.error("Error fetching contact attempts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user || !profile) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("applicant_contact_attempts")
        .insert({
          applicant_id: applicantId,
          contact_type: formData.contact_type,
          notes: formData.notes || null,
          contacted_at: formData.contacted_at,
          contacted_by: profile.full_name || user.email || "Unbekannt",
          contacted_by_user_id: user.id,
        });

      if (error) throw error;

      toast.success("Kontaktaufnahme erfasst");
      setFormData({
        contact_type: "Anruf",
        notes: "",
        contacted_at: new Date().toISOString().slice(0, 16),
      });
      setShowForm(false);
      fetchAttempts();
    } catch (error) {
      console.error("Error saving contact attempt:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("applicant_contact_attempts")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Eintrag gelöscht");
      fetchAttempts();
    } catch (error) {
      console.error("Error deleting contact attempt:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleQuickAdd = async () => {
    if (!user || !profile) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("applicant_contact_attempts")
        .insert({
          applicant_id: applicantId,
          contact_type: "Anruf",
          notes: null,
          contacted_at: new Date().toISOString(),
          contacted_by: profile.full_name || user.email || "Unbekannt",
          contacted_by_user_id: user.id,
        });

      if (error) throw error;

      toast.success("Kontaktversuch erfasst");
      fetchAttempts();
    } catch (error) {
      console.error("Error saving quick contact:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Kontaktaufnahme
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleQuickAdd}
              disabled={saving}
            >
              <Clock className="h-4 w-4 mr-1" />
              Jetzt
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(!showForm)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Eintrag
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kontaktart</Label>
                <Select
                  value={formData.contact_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, contact_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zeitpunkt</Label>
                <Input
                  type="datetime-local"
                  value={formData.contacted_at}
                  onChange={(e) =>
                    setFormData({ ...formData, contacted_at: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notizen (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="z.B. Nicht erreicht, Mailbox besprochen..."
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(false)}
              >
                Abbrechen
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={saving}>
                Speichern
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-muted-foreground py-4">
            Lädt...
          </div>
        ) : attempts.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            Noch keine Kontaktversuche erfasst
          </div>
        ) : (
          <div className="space-y-2">
            {attempts.map((attempt) => (
              <div
                key={attempt.id}
                className="flex items-start justify-between p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">
                      {attempt.contact_type}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(attempt.contacted_at), "dd.MM.yyyy HH:mm", {
                        locale: de,
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <User className="h-3 w-3" />
                    {attempt.contacted_by}
                  </div>
                  {attempt.notes && (
                    <div className="flex items-start gap-1 text-sm mt-2 text-foreground/80">
                      <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="break-words">{attempt.notes}</span>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                  onClick={() => handleDelete(attempt.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApplicantContactAttempts;
